import { useLazyQuery, useQuery } from '@apollo/client';
import { HandCoins, Printer, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CUSTOMERS_QUERY } from '@/modules/customers-pets/customers.gql';
import {
  CUSTOMER_ABONO_HISTORY_QUERY,
  OPEN_ACCOUNTS_QUERY,
} from '@/modules/open-accounts/open-accounts.gql';
import { ApplyCustomerAbonoDialog } from './ApplyCustomerAbonoDialog';
import { CUSTOMER_STATEMENT_QUERY } from './customer-statement.gql';
import {
  buildCustomerStatementPrintHtml,
  printHtmlInHiddenWindow,
  type CustomerStatementPrintStrings,
} from './customer-statement-print';
import { buildStatementView, type StatementView } from './customer-statement-view';
import type { CustomerStatement } from './types';

import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { useAuthStore } from '@/stores/authStore';

interface CustomerLite {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string;
}

interface CustomersResp {
  customers: { items: CustomerLite[] };
}

interface CustomerStatementResp {
  customerStatementReport: CustomerStatement;
}

interface OpenAccountLite {
  id: string;
  folio_number: string | null;
  kind: string;
  status: string;
  balance_cents: string;
  pet_id: string;
  pet_name: string | null;
  opened_at: string;
}

interface OpenAccountsResp {
  openAccounts: { items: OpenAccountLite[] };
}

interface AbonoHistoryRow {
  payment_id: string;
  payment_folio_number: string | null;
  payment_method_code: string;
  payment_method_name: string;
  amount_cents: string;
  reference_number: string | null;
  captured_at: string;
  open_account_id: string;
  account_folio_number: string | null;
  pet_name: string | null;
}

interface AbonoHistoryResp {
  customerAbonoHistory: AbonoHistoryRow[];
}

const CONDITION_BADGE_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  liquidado: 'success',
  parcial: 'warning',
  pendiente: 'destructive',
  sin_saldo: 'muted',
};

const HISTORY_DATE_FORMAT = new Intl.DateTimeFormat('es-MX', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function CustomerStatementPage(): JSX.Element {
  const { t } = useTranslation();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const can_view_report = permissions.includes('admin.reports.view');
  const can_manage_abono = permissions.includes('open_accounts.payment');
  const can_view_accounts = permissions.includes('open_accounts.read');

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [customer_id, setCustomerId] = useState<string | null>(null);
  const [selected_customer, setSelectedCustomer] = useState<CustomerLite | null>(null);
  const [printing, setPrinting] = useState(false);
  const [show_abono_dialog, setShowAbonoDialog] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data: customers_data, loading: customers_loading } = useQuery<CustomersResp>(
    CUSTOMERS_QUERY,
    {
      variables: { offset: 0, limit: 8, search: debounced || null, status_filter: 'ACTIVE' },
      skip: !!customer_id || debounced.length < 2,
    },
  );

  const [loadStatement, { data, loading, error }] = useLazyQuery<CustomerStatementResp>(
    CUSTOMER_STATEMENT_QUERY,
    { fetchPolicy: 'network-only' },
  );

  const {
    data: accounts_data,
    loading: accounts_loading,
    refetch: refetchAccounts,
  } = useQuery<OpenAccountsResp>(OPEN_ACCOUNTS_QUERY, {
    variables: { offset: 0, limit: 50, customer_id, status: 'OPEN' },
    skip: !customer_id || !can_view_accounts,
    fetchPolicy: 'network-only',
  });

  const { data: history_data, refetch: refetchHistory } = useQuery<AbonoHistoryResp>(
    CUSTOMER_ABONO_HISTORY_QUERY,
    {
      variables: { customer_id },
      skip: !customer_id || !can_view_accounts,
      fetchPolicy: 'network-only',
    },
  );

  const onClickPickCustomer = (customer: CustomerLite): void => {
    setCustomerId(customer.id);
    setSelectedCustomer(customer);
    setSearch('');
    if (can_view_report) void loadStatement({ variables: { customer_id: customer.id } });
  };

  const onClickClearCustomer = (): void => {
    setCustomerId(null);
    setSelectedCustomer(null);
    setSearch('');
  };

  const view: StatementView | null = useMemo(() => {
    if (!data?.customerStatementReport) return null;
    return buildStatementView(data.customerStatementReport, t);
  }, [data, t]);

  const sorted_open_accounts = useMemo(() => {
    const items = accounts_data?.openAccounts.items ?? [];
    return [...items].sort(
      (a, b) => new Date(a.opened_at).getTime() - new Date(b.opened_at).getTime(),
    );
  }, [accounts_data]);

  // Abonar only ever touches `kind: normal` accounts (hospitalization keeps its own
  // dedicated deposit/minimum flow under "Cuentas abiertas") — but the list above shows
  // EVERY open account so its total always matches the report's "Saldo pendiente"
  // headline (which sums all kinds). Splitting the two avoids a confusing mismatch
  // between "this is what the customer owes" and "this is what Abonar can collect".
  const abono_eligible_accounts = sorted_open_accounts.filter(
    (account) => account.kind === 'NORMAL',
  );
  const has_non_normal_accounts = sorted_open_accounts.some(
    (account) => account.kind !== 'NORMAL',
  );

  const total_open_balance_cents = sorted_open_accounts
    .reduce((acc, account) => acc + BigInt(account.balance_cents), 0n)
    .toString();
  const abono_eligible_balance_cents = abono_eligible_accounts
    .reduce((acc, account) => acc + BigInt(account.balance_cents), 0n)
    .toString();

  const onAbonoSubmitted = async (): Promise<void> => {
    await Promise.all([refetchAccounts(), refetchHistory()]);
    if (can_view_report && customer_id) {
      void loadStatement({ variables: { customer_id } });
    }
  };

  const onClickPrint = async (): Promise<void> => {
    if (!view) return;
    setPrinting(true);
    try {
      const strings: CustomerStatementPrintStrings = {
        title: t('customer_statement.print_title'),
        generated_at_prefix: t('customer_statement.generated_at_label'),
        phone_label: t('customer_statement.phone_label'),
        email_label: t('customer_statement.email_label'),
        tickets_label: t('customer_statement.tickets_label'),
        liquidados_label: t('customer_statement.liquidados_label'),
        parciales_label: t('customer_statement.parciales_label'),
        pendientes_label: t('customer_statement.pendientes_label'),
        sin_saldo_label: t('customer_statement.sin_saldo_label'),
        total_charges_label: t('customer_statement.total_charges_label'),
        paid_label: t('customer_statement.paid_label'),
        pending_balance_label: t('customer_statement.pending_balance_label'),
        ticket_col: t('customer_statement.ticket_col'),
        date_col: t('customer_statement.date_col'),
        pet_col: t('customer_statement.pet_col'),
        status_col: t('customer_statement.status_col'),
        code_col: t('customer_statement.code_col'),
        concept_col: t('customer_statement.concept_col'),
        qty_col: t('customer_statement.qty_col'),
        unit_price_col: t('customer_statement.unit_price_col'),
        line_total_col: t('customer_statement.line_total_col'),
        ticket_subtotal_label: t('customer_statement.ticket_subtotal_label'),
        ticket_tax_label: t('customer_statement.ticket_tax_label'),
        ticket_total_label: t('customer_statement.ticket_total_label'),
        ticket_paid_label: t('customer_statement.ticket_paid_label'),
        ticket_pending_label: t('customer_statement.ticket_pending_label'),
        closing_title: t('customer_statement.closing_title'),
        no_tickets_label: t('customer_statement.no_tickets'),
      };
      const html = buildCustomerStatementPrintHtml({ view, strings });
      await printHtmlInHiddenWindow(html);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('customer_statement.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('customer_statement.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('customer_statement.pick_customer')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {customer_id && selected_customer ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card/40 p-3">
              <div>
                <p className="font-medium">
                  {selected_customer.first_name}
                  {selected_customer.last_name ? ` ${selected_customer.last_name}` : ''}
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                  {selected_customer.phone}
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={onClickClearCustomer}>
                {t('customer_statement.change_customer')}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder={t('customer_statement.search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {debounced.length >= 2 ? (
                <div className="space-y-1">
                  {customers_loading ? (
                    <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
                  ) : null}
                  {customers_data?.customers.items.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onClickPickCustomer(c)}
                      className="flex w-full items-center justify-between rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border hover:bg-secondary"
                    >
                      <span>
                        {c.first_name}
                        {c.last_name ? ` ${c.last_name}` : ''}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{c.phone}</span>
                    </button>
                  ))}
                  {(customers_data?.customers.items.length ?? 0) === 0 && !customers_loading ? (
                    <p className="text-xs text-muted-foreground">
                      {t('customer_statement.no_customer_match')}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {customer_id && can_view_accounts ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">{t('customer_statement.accounts_title')}</CardTitle>
            {can_manage_abono ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setShowAbonoDialog(true)}
                disabled={abono_eligible_accounts.length === 0}
                title={
                  abono_eligible_accounts.length === 0 && sorted_open_accounts.length > 0
                    ? t('customer_statement.abono_button_disabled_hint')
                    : undefined
                }
              >
                <HandCoins className="mr-2 h-4 w-4" aria-hidden />
                {t('customer_statement.abono_button')}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {accounts_loading ? (
              <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
            ) : null}
            {sorted_open_accounts.length === 0 && !accounts_loading ? (
              <p className="text-sm text-muted-foreground">
                {t('customer_statement.no_open_accounts')}
              </p>
            ) : (
              <ul className="space-y-2">
                {sorted_open_accounts.map((account) => (
                  <li
                    key={account.id}
                    className="flex items-center justify-between rounded-md border border-border bg-card/40 p-2 text-sm"
                  >
                    <div>
                      <span className="font-medium">
                        {account.pet_name ?? t('customer_statement.unknown_pet')}
                      </span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {account.folio_number ? `#${account.folio_number}` : ''}
                      </span>
                      <Badge variant="outline" className="ml-2">
                        {t(`open_accounts.kind_${account.kind.toLowerCase()}`)}
                      </Badge>
                    </div>
                    <span className="font-mono">{formatCentsString(account.balance_cents)}</span>
                  </li>
                ))}
              </ul>
            )}
            {sorted_open_accounts.length > 0 ? (
              <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
                <span>{t('customer_statement.total_open_balance')}</span>
                <span className="font-mono">{formatCentsString(total_open_balance_cents)}</span>
              </div>
            ) : null}
            {has_non_normal_accounts ? (
              <p className="text-xs text-muted-foreground">
                {t('customer_statement.hospitalization_note')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {customer_id && can_view_accounts ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('customer_statement.abono_history_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {(history_data?.customerAbonoHistory.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('customer_statement.no_abono_history')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1 pr-2">{t('customer_statement.abono_date_col')}</th>
                      <th className="py-1 pr-2">{t('customer_statement.pet_col')}</th>
                      <th className="py-1 pr-2">{t('customer_statement.payment_method_label')}</th>
                      <th className="py-1 pr-2">{t('customer_statement.reference_label')}</th>
                      <th className="py-1 text-right">{t('customer_statement.amount_label')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(history_data?.customerAbonoHistory ?? []).map((row) => (
                      <tr key={row.payment_id} className="border-b last:border-0">
                        <td className="py-1 pr-2">
                          {HISTORY_DATE_FORMAT.format(new Date(row.captured_at))}
                        </td>
                        <td className="py-1 pr-2">{row.pet_name ?? '-'}</td>
                        <td className="py-1 pr-2">{row.payment_method_name}</td>
                        <td className="py-1 pr-2">{row.reference_number ?? '-'}</td>
                        <td className="py-1 text-right font-mono">
                          {formatCentsString(row.amount_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {show_abono_dialog && customer_id ? (
        <ApplyCustomerAbonoDialog
          customer_id={customer_id}
          total_balance_cents={abono_eligible_balance_cents}
          on_close={() => setShowAbonoDialog(false)}
          on_submitted={async () => {
            await onAbonoSubmitted();
            setShowAbonoDialog(false);
          }}
        />
      ) : null}

      {can_view_report && loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : null}
      {can_view_report && error ? (
        <p className="text-sm text-destructive">{t('customer_statement.load_error')}</p>
      ) : null}

      {view ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {t('customer_statement.generated_at_label')}:{' '}
              <span className="font-medium text-foreground">{view.generated_at_label}</span>
            </p>
            <Button type="button" onClick={() => void onClickPrint()} disabled={printing}>
              <Printer className="mr-2 h-4 w-4" aria-hidden />
              {t('customer_statement.print_button')}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('customer_statement.summary_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
                <SummaryStat label={t('customer_statement.tickets_label')} value={String(view.summary.tickets_count)} />
                <SummaryStat label={t('customer_statement.liquidados_label')} value={String(view.summary.liquidados_count)} />
                <SummaryStat label={t('customer_statement.parciales_label')} value={String(view.summary.parciales_count)} />
                <SummaryStat label={t('customer_statement.pendientes_label')} value={String(view.summary.pendientes_count)} />
                <SummaryStat label={t('customer_statement.total_charges_label')} value={view.summary.total_label} />
                <SummaryStat label={t('customer_statement.paid_label')} value={view.summary.paid_label} />
                <SummaryStat
                  label={t('customer_statement.pending_balance_label')}
                  value={view.summary.pending_label}
                  emphasize
                />
              </div>
            </CardContent>
          </Card>

          {view.tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('customer_statement.no_tickets')}</p>
          ) : (
            <div className="space-y-3">
              {view.tickets.map((ticket) => (
                <Card key={ticket.sale_order_id}>
                  <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
                    <div>
                      <CardTitle className="text-base">{ticket.ticket_label}</CardTitle>
                      <p className="text-xs text-muted-foreground">{ticket.date_label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{ticket.pet_name}</Badge>
                      <Badge variant="outline">{ticket.status_label}</Badge>
                      <Badge variant={CONDITION_BADGE_VARIANT[ticket.condition] ?? 'muted'}>
                        {ticket.condition_label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                            <th className="py-1 pr-2">{t('customer_statement.code_col')}</th>
                            <th className="py-1 pr-2">{t('customer_statement.concept_col')}</th>
                            <th className="py-1 pr-2 text-right">{t('customer_statement.qty_col')}</th>
                            <th className="py-1 pr-2 text-right">
                              {t('customer_statement.unit_price_col')}
                            </th>
                            <th className="py-1 text-right">{t('customer_statement.line_total_col')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticket.lines.map((line, index) => (
                            <tr key={`${ticket.sale_order_id}-${index}`} className="border-b last:border-0">
                              <td className="py-1 pr-2 font-mono text-xs">{line.item_code}</td>
                              <td className="py-1 pr-2">{line.item_name}</td>
                              <td className="py-1 pr-2 text-right">{line.quantity}</td>
                              <td className="py-1 pr-2 text-right">{line.unit_price_label}</td>
                              <td className="py-1 text-right">{line.total_label}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-md bg-secondary/40 px-3 py-2 text-xs">
                      <span>{t('customer_statement.ticket_subtotal_label')}: {ticket.subtotal_label}</span>
                      <span>{t('customer_statement.ticket_tax_label')}: {ticket.tax_label}</span>
                      <span>{t('customer_statement.ticket_total_label')}: {ticket.total_label}</span>
                      <span>{t('customer_statement.ticket_paid_label')}: {ticket.paid_label}</span>
                      <span className="font-medium">
                        {t('customer_statement.ticket_pending_label')}: {ticket.pending_label}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('customer_statement.closing_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <p>{t('customer_statement.total_charges_label')}: {view.summary.total_label}</p>
              <p>{t('customer_statement.paid_label')}: {view.summary.paid_label}</p>
              <p className="font-semibold">
                {t('customer_statement.pending_balance_label')}: {view.summary.pending_label}
              </p>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={emphasize ? 'text-lg font-bold text-destructive' : 'text-lg font-semibold'}>
        {value}
      </p>
    </div>
  );
}
